import {
  FormFieldBehavior,
  FormFieldSchema,
  FormFieldType,
  TriggerCondition,
  TriggerType,
} from "@app/components/forms/types/schema";
import { z } from "zod";
import { useForm, UseFormProps } from "react-hook-form";
import { generateZodSchema } from "@app/components/forms/lib/generateZodSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";

const makeDefaultValues = (schemas: FormFieldSchema[]) => {
  const defaultValues: Record<string, any> = {};
  if (!schemas || !schemas.length) return defaultValues;

  schemas.forEach((schema) => {
    if (schema.type === FormFieldType.Group) {
      Object.assign(defaultValues, makeDefaultValues(schema.schemas));
    } else if (schema.type !== FormFieldType.Separator) {
      defaultValues[schema.id] = schema.defaultValue ?? "";
    }
  });

  return defaultValues;
};

type FormValues<T> = T | undefined;

interface UseFormWithSchemaProps<T> extends UseFormProps {
  formSchemas: FormFieldSchema[];
  defaultFormValues?: T; // 연관된 폼끼리 트리거 처리를 위해 필요
}

export const useFormWithSchema = <T extends Record<string, any> = {}>(
  props: UseFormWithSchemaProps<T>,
) => {
  const { formSchemas, defaultFormValues, ...rest } = props;
  const [schemas, setSchemas] = useState<FormFieldSchema[]>(formSchemas);
  const [zodSchema, setZodSchema] = useState(generateZodSchema(formSchemas));
  const [formValues, setFormValues] = useState<T>(
    defaultFormValues ?? ({} as T),
  );

  const form = useForm<z.infer<typeof zodSchema>>({
    resolver: zodResolver(zodSchema),
    ...rest,
    defaultValues: makeDefaultValues(schemas),
    mode: props.mode ?? "onChange",
  });

  useEffect(() => {
    executeFieldBehaviorTriggers(formValues ?? form.getValues());
  }, []);

  useEffect(() => {
    setZodSchema(generateZodSchema(schemas));
  }, [schemas]);

  useEffect(() => {
    form.watch((value) => {
      setFormValues((prev) => {
        return { ...prev, ...value };
      });
    });
  }, []);

  useEffect(() => {
    form.watch((value) => {
      executeFieldBehaviorTriggers({
        ...formValues,
        ...value,
      });
    });
  }, []);

  useEffect(() => {
    // if (!formValues) return;

    const values = form.getValues();
    for (const key in values) {
      if (formValues[key] !== values[key]) {
        form.setValue(key, formValues[key]);
      }
    }

    executeFieldBehaviorTriggers({
      ...formValues,
      ...form.getValues(),
    });
  }, [formValues]);

  const setDefaultValues = (defaultValues: Record<string, any>) => {
    form.reset(defaultValues);
    executeFieldBehaviorTriggers({
      ...formValues,
      ...form.getValues(),
    });
  };

  const reset = (defaultValues: any) => {
    form.reset(defaultValues);
  };

  // 타입 가져올 수 있는 지 확인해보기
  const parse = (): z.SafeParseReturnType<
    { [p: string]: any },
    { [p: string]: any }
  > => {
    const result = zodSchema.safeParse(form.getValues());
    if (!result.success) {
      result.error.issues.forEach((error) => {
        form.setError(error.path.join("."), {
          type: "manual",
          message: error.message,
        });
      });
    }
    return result;
  };

  const updateField = (id: string, updates: Partial<FormFieldSchema>) => {
    const schema: FormFieldSchema[] = schemas.map((schema: any) => {
      if (schema.id === id) {
        return { ...schema, ...updates };
      }
      return schema;
    });

    setSchemas(schema);
  };

  const getSchemaById = (id: string) => {
    return schemas.find((schema) => schema.id === id);
  };

  const addField = (schema: FormFieldSchema) => {
    setSchemas([...schemas, schema]);
  };

  const removeField = (id: string) => {
    setSchemas(schemas.filter((schema) => schema.id !== id));
  };

  const setFieldBehavior = (
    id: string,
    behavior: Partial<FormFieldBehavior["behavior"]>,
  ) => {
    const schema = getSchemaById(id);
    if (!schema) return;
    updateField(id, { behavior: { ...schema.behavior, ...behavior } });
  };

  const setFieldBehaviors = (behaviors: Map<string, any>) => {
    // setSchemas((prev: FormFieldSchema[]): FormFieldSchema[] => {
    //   return prev.map(schema => {
    //     if (!schema.id) return schema;
    //
    //     if (behaviors.has(schema.id)) {
    //       return { ...schema, ...{ behavior: { ...schema.behavior, ...behaviors.get(schema.id) } } };
    //     }
    //     return schema;
    //   }) as FormFieldSchema[];
    // });

    const replaceSchema = (schemas: FormFieldSchema[]) => {
      return schemas.map((schema: FormFieldSchema): any => {
        if (!schema.id) return schema;

        if (behaviors.has(schema.id)) {
          return {
            ...schema,
            ...{
              behavior: { ...schema.behavior, ...behaviors.get(schema.id) },
            },
          };
        }

        if (schema.type === FormFieldType.Group) {
          return { ...schema, schemas: replaceSchema(schema.schemas) };
        }

        return schema;
      }) as FormFieldSchema[];
    };

    const newSchemas = replaceSchema(schemas);
    setSchemas(newSchemas);
  };

  const evaluateCondition = (
    value: any,
    condition: string,
    targetValue: any,
  ) => {
    switch (condition) {
      case TriggerCondition.Equal:
        return value === targetValue;
      case TriggerCondition.NotEqual:
        return value !== targetValue;
      case TriggerCondition.GreaterThan:
        return value > targetValue;
      case TriggerCondition.LessThan:
        return value < targetValue;
      case TriggerCondition.GreaterThanOrEqual:
        return value >= targetValue;
      case TriggerCondition.LessThanOrEqual:
        return value <= targetValue;
      default:
        return false;
    }
  };

  const executeFieldBehaviorTriggers = (fieldValues: any) => {
    const behaviors: Map<string, any> = new Map();

    const executor = (schemas: FormFieldSchema[]) => {
      schemas.forEach((schema) => {
        if (!schema.id) return;

        schema.type === FormFieldType.Group && executor(schema.schemas);

        if (!schema.behavior?.triggers) return;
        schema.behavior?.triggers?.forEach((option) => {
          const { type, field, condition, value: targetValue } = option;
          const value = fieldValues[field];
          const shouldDisable = !evaluateCondition(
            value,
            condition,
            targetValue,
          );

          if (type === TriggerType.Show) {
            // setFieldBehavior(schema.id!, { hidden: shouldDisable });
            behaviors.set(schema.id!, { hidden: shouldDisable });
          } else if (type === TriggerType.Enable) {
            // setFieldBehavior(schema.id!, { disabled: shouldDisable });
            behaviors.set(schema.id!, { disabled: shouldDisable });
          }
        });
      });
    };

    executor(formSchemas);
    //
    // formSchemas.forEach(schema => {
    //   if (!schema.id) return;
    //   if (!schema.behavior?.triggers) return;
    //
    //   schema.behavior?.triggers?.forEach(option => {
    //     const { type, field, condition, value: targetValue } = option;
    //     const value = fieldValues[field];
    //     const shouldDisable = !evaluateCondition(value, condition, targetValue);
    //
    //     if (type === TriggerType.Show) {
    //       // setFieldBehavior(schema.id!, { hidden: shouldDisable });
    //       behaviors.set(schema.id!, { hidden: shouldDisable });
    //     } else if (type === TriggerType.Enable) {
    //       // setFieldBehavior(schema.id!, { disabled: shouldDisable });
    //       behaviors.set(schema.id!, { disabled: shouldDisable });
    //     }
    //   });
    // });

    // bulk update
    setFieldBehaviors(behaviors);
  };

  const watch = (cb: (data?: any) => void) => {
    form.watch((value) => {
      return cb({ ...formValues, ...value });
    });
  };

  return {
    form,
    reset,
    watchForm: watch,
    schemas,
    zodSchema,
    parseForm: parse,
    formValues,
    setFormValues,
  };
};
